import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { catchError, finalize, map, of, switchMap } from 'rxjs';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import {
  MonthNavigationComponent,
  formatMonthKeyFromDate,
  formatMonthLabel,
  getNextMonthKey,
  getPreviousMonthKey,
  normalizeMonthKey,
} from 'src/app/components/main/shared/month-navigation/month-navigation.component';
import { MetricCardComponent } from 'src/app/components/main/shared/metric-card/metric-card.component';
import { CompanyProductPageIdentityComponent } from '../company-product-detail/company-product-page-identity/company-product-page-identity.component';
import { HtmlDialogComponent } from 'src/app/components/main/shared/html-dialog/html-dialog.component';
import {
  buildConciliationDateRange,
  buildInvoiceLineLabel,
  isInvoiceDateWithinConciliationRange,
  resolveCompanyProductIdFromRouteAncestors,
} from '../company-product-monthly-economic-report-conciliation/company-product-monthly-economic-report-conciliation.component';
import { Invoice } from 'src/app/models/invoice.model';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { CompanyProductMonthlyEconomicReport } from 'src/app/models/company_product_monthly_economic_report.model';
import { ApiService, UtilsService } from 'src/app/services';

type BillingViewMode = 'period' | 'invoices';

interface BillingPeriodSerialized {
  id: number;
  company_product_id: number;
  company_product_title: string;
  company_product_logo_url: string | null;
  billed_start_date: string;
  month_label: string;
  hours_expected_label: string;
  hours_registered_label: string;
  hours_billed_label: string;
  hours_expected_progress: number;
  hours_registered_progress: number;
  hours_billed_progress: number;
  real_price_per_hour_label: string;
  invoice_price_per_hour_label: string;
  payment_status_label: string;
  payment_status_class: string;
  conciliation_status_label: string;
  conciliation_status_class: string;
  payment_status_icon: string | null;
  payment_status_tooltip: string | null;
}

interface BillingInvoiceSerialized {
  id: number;
  company_product_id: number | null;
  company_product_title: string;
  company_product_logo_url: string | null;
  month_label: string;
  invoice_number: string;
  document_status_badge: string | null;
  document_status_tooltip: string | null;
  document_status_class: string | null;
  hours_billed_label: string;
  hours_conciliated_label: string;
  is_conciliation_pending: boolean;
  period_conciliations: InvoicePeriodConciliationRow[];
}

interface InvoicePeriodConciliationRow {
  period_label: string;
  invoice_line_label: string | null;
  hours_conciliated_label: string;
  amount_conciliated_label: string;
  origin_label: string;
}

interface BillingSummaryCards {
  missing_invoice_count: number;
  overdue_payment_count: number;
  pending_conciliation_count: number;
}

interface CompanyProductLookup {
  id: number;
  title: string;
  logo_url: string | null;
}

@Component({
  selector: 'app-company-product-billing-periods',
  templateUrl: './company-product-billing-periods.component.html',
  styleUrl: './company-product-billing-periods.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
    TypeSafeMatCellDeferred,
    MonthNavigationComponent,
    MetricCardComponent,
    CompanyProductPageIdentityComponent,
  ],
})
export class CompanyProductBillingPeriodsComponent extends IndexTemplateComponent {
  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly dom_sanitizer = inject(DomSanitizer);
  private readonly reload_trigger = signal(0);
  protected readonly form_builder = inject(UntypedFormBuilder);
  protected readonly router = inject(Router);
  protected readonly activated_route = inject(ActivatedRoute);
  protected readonly utils_service = inject(UtilsService);

  private readonly query_params = toSignal(this.activated_route.queryParamMap, {
    initialValue: this.activated_route.snapshot.queryParamMap,
  });
  private readonly current_month_key = formatMonthKeyFromDate(new Date());
  private readonly latest_navigable_month_key = getNextMonthKey(this.current_month_key);

  readonly can_go_to_next_month = computed((): boolean => this.selected_month() < this.latest_navigable_month_key);
  readonly can_go_to_current_month = computed((): boolean => this.selected_month() !== this.current_month_key);

  readonly fixed_company_product_id = signal<number | null>(resolveCompanyProductIdFromRouteAncestors(this.activated_route));
  readonly show_company_product_filter = computed((): boolean => this.fixed_company_product_id() === null);
  readonly show_month_navigation = computed((): boolean => this.fixed_company_product_id() === null);
  readonly show_month_column = computed((): boolean => this.fixed_company_product_id() !== null);
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  readonly view_mode = computed((): BillingViewMode => {
    const view = this.query_params().get('view');
    return view === 'invoices' ? 'invoices' : 'period';
  });

  readonly is_invoices_view = computed((): boolean => this.view_mode() === 'invoices');

  readonly filter_company_product_id = computed((): number | null => {
    const fixed_company_product_id = this.fixed_company_product_id();
    if (fixed_company_product_id !== null) {
      return fixed_company_product_id;
    }

    return this.parseOptionalNumber(this.query_params().get('company_product_id'));
  });

  readonly displayed_columns = computed((): string[] => {
    const columns: string[] = [];
    if (this.show_month_column()) {
      columns.push('month');
    }
    if (this.filter_company_product_id() === null) {
      columns.push('company_product');
    }
    columns.push('hours', 'real_price_per_hour', 'invoice_price_per_hour', 'payment_status', 'conciliation_status', 'actions');
    return columns;
  });

  readonly invoice_displayed_columns = computed((): string[] => {
    const columns: string[] = [];
    if (this.show_month_column()) {
      columns.push('month');
    }
    if (this.filter_company_product_id() === null) {
      columns.push('company_product');
    }
    columns.push('invoice_number', 'hours_billed', 'hours_conciliated', 'actions');
    return columns;
  });

  readonly selected_month = computed((): string => {
    const query_month = this.query_params().get('month');
    const requested_month = normalizeMonthKey(query_month) ?? this.current_month_key;
    if (requested_month > this.latest_navigable_month_key) {
      return this.latest_navigable_month_key;
    }

    return requested_month;
  });

  dataSource = new MatTableDataSource<BillingPeriodSerialized>([]);
  invoice_data_source = new MatTableDataSource<BillingInvoiceSerialized>([]);

  readonly summary_cards = signal<BillingSummaryCards>({
    missing_invoice_count: 0,
    overdue_payment_count: 0,
    pending_conciliation_count: 0,
  });

  readonly is_loading = signal(false);
  readonly is_invoices_loading = signal(false);

  private readonly company_products_by_holded_account = toSignal(
    toObservable(computed(() => this.view_mode() === 'invoices' && this.filter_company_product_id() === null)).pipe(
      switchMap(should_load => {
        if (!should_load) {
          return of(new Map<string, CompanyProductLookup>());
        }

        return this.api_service.getCompanyProducts({ per_page: 500 }).pipe(
          map(response => buildCompanyProductsByHoldedAccountMap(response.data)),
          catchError(() => of(new Map<string, CompanyProductLookup>()))
        );
      })
    ),
    { initialValue: new Map<string, CompanyProductLookup>() }
  );

  private readonly fetch_request = computed(() => {
    if (this.view_mode() !== 'period') {
      return null;
    }

    this.reload_trigger();
    const company_product_id_filter = this.filter_company_product_id();
    const selected_month = this.selected_month();
    const is_multi_month_view = this.fixed_company_product_id() !== null;

    let billed_from: string;
    let billed_to: string;

    if (is_multi_month_view) {
      const date_range = buildConciliationDateRange();
      billed_from = date_range.billed_from;
      billed_to = date_range.billed_to;
    } else {
      billed_from = `${selected_month}-01`;
      billed_to = dayjs(`${selected_month}-01`).endOf('month').format('YYYY-MM-DD');
    }

    const params: Record<string, unknown> = {
      billed_from,
      billed_to,
      with: 'company_product,company_product_monthly_economic_report_invoice_conciliations.invoice,company_product_monthly_economic_report_invoice_conciliations.user',
      append: COMPANY_PRODUCT_MONTHLY_ECONOMIC_REPORT_BILLING_PERIODS_APPEND,
      order_by: 'billed_start_date:desc',
      per_page: 200,
      page: 1,
    };

    if (company_product_id_filter !== null) {
      params['company_product_id'] = company_product_id_filter;
    }

    return { params, selected_month, is_multi_month_view };
  });

  private readonly invoices_fetch_request = computed(() => {
    if (this.view_mode() !== 'invoices') {
      return null;
    }

    this.reload_trigger();
    const company_product_id_filter = this.filter_company_product_id();
    const selected_month = this.selected_month();
    const is_multi_month_view = this.fixed_company_product_id() !== null;

    const params: Record<string, unknown> = {
      with: 'invoice_lines,company_product_monthly_economic_report_invoice_conciliations.invoice_line,company_product_monthly_economic_report_invoice_conciliations.company_product_monthly_economic_report.company_product,rectifies_invoice,rectified_by_credit_notes',
      append: 'ro_total_hours,ro_hours_conciliated,ro_is_fully_conciliated,is_cancelled,is_credit_note,is_rectified,is_conciliable',
      order_by: 'invoice_date:desc',
      per_page: 200,
      page: 1,
    };

    if (company_product_id_filter !== null) {
      params['company_product_id'] = company_product_id_filter;
    }

    return { params, selected_month, is_multi_month_view };
  });

  private readonly reports_response = toSignal(
    toObservable(this.fetch_request).pipe(
      switchMap(request => {
        if (request === null) {
          return of({
            data: [] as CompanyProductMonthlyEconomicReport[],
            meta: { total: 0, current_page: 1, per_page: 200 },
          });
        }

        this.is_loading.set(true);
        return this.api_service.getCompanyProductMonthlyEconomicReports(request.params).pipe(
          catchError(() => of({ data: [] as CompanyProductMonthlyEconomicReport[], meta: { total: 0, current_page: 1, per_page: 200 } })),
          finalize(() => this.is_loading.set(false))
        );
      })
    ),
    {
      initialValue: {
        data: [] as CompanyProductMonthlyEconomicReport[],
        meta: { total: 0, current_page: 1, per_page: 200 },
      },
    }
  );

  private readonly invoices_response = toSignal(
    toObservable(this.invoices_fetch_request).pipe(
      switchMap(request => {
        if (request === null) {
          return of({ data: [] as Invoice[] });
        }

        this.is_invoices_loading.set(true);
        return this.api_service.getInvoices(request.params).pipe(
          map(response => ({
            data: this.filterInvoicesByDateRange(response.data, request.selected_month, request.is_multi_month_view),
          })),
          catchError(() => of({ data: [] as Invoice[] })),
          finalize(() => this.is_invoices_loading.set(false))
        );
      })
    ),
    { initialValue: { data: [] as Invoice[] } }
  );

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));

    effect(() => {
      if (this.view_mode() !== 'period') {
        return;
      }

      const response = this.reports_response();
      const request = this.fetch_request();
      if (request === null) {
        return;
      }

      const serialized_rows = this.serializeReports(response.data, request.selected_month, request.is_multi_month_view);
      this.dataSource.data = serialized_rows;
      this.summary_cards.set(this.buildSummaryCards(response.data, request.selected_month, request.is_multi_month_view));
      this.change_detector_ref.markForCheck();
    });

    effect(() => {
      if (this.view_mode() !== 'invoices') {
        return;
      }

      const response = this.invoices_response();
      const company_products_by_holded_account = this.company_products_by_holded_account();
      const serialized_rows = response.data.map(invoice => this.serializeInvoice(invoice, company_products_by_holded_account));
      this.invoice_data_source.data = serialized_rows;
      this.change_detector_ref.markForCheck();
    });
  }

  override fetchData(): void {
    this.reload_trigger.update(value => value + 1);
  }

  onViewModeChange(is_invoices_view: boolean): void {
    this.router.navigate([], {
      relativeTo: this.activated_route,
      queryParams: {
        view: is_invoices_view ? 'invoices' : null,
      },
      queryParamsHandling: 'merge',
    });
  }

  goToPreviousMonth(): void {
    this.updateMonthQueryParam(getPreviousMonthKey(this.selected_month()));
  }

  goToNextMonth(): void {
    if (!this.can_go_to_next_month()) {
      return;
    }

    this.updateMonthQueryParam(getNextMonthKey(this.selected_month()));
  }

  goToCurrentMonth(): void {
    this.updateMonthQueryParam(this.current_month_key);
  }

  private updateMonthQueryParam(month: string): void {
    this.router.navigate([], {
      relativeTo: this.activated_route,
      queryParams: {
        month: month === this.current_month_key ? null : month,
      },
      queryParamsHandling: 'merge',
    });
  }

  navigateToConciliation(company_product_id: number): void {
    const current_query_params = this.activated_route.snapshot.queryParams;

    this.router.navigate(['/employees', 'production', 'company-product-monthly-economic-reports', 'conciliation'], {
      queryParams: {
        ...current_query_params,
        company_product_id,
      },
    });
  }

  navigateToDetail(pmer_id: number): void {
    const current_query_params = this.activated_route.snapshot.queryParams;
    const fixed_company_product_id = this.fixed_company_product_id();
    if (fixed_company_product_id !== null) {
      this.router.navigate(
        ['/employees', 'development', 'company-products', fixed_company_product_id, 'company-product-monthly-economic-reports', pmer_id],
        {
          queryParams: current_query_params,
        }
      );
      return;
    }
    this.router.navigate(['/employees', 'production', 'company-product-monthly-economic-reports', pmer_id], {
      queryParams: current_query_params,
    });
  }

  navigateToCompanyProductConciliation(): void {
    const fixed_company_product_id = this.fixed_company_product_id();
    if (fixed_company_product_id === null) {
      return;
    }
    const current_query_params = this.activated_route.snapshot.queryParams;
    this.router.navigate(
      [
        '/employees',
        'development',
        'company-products',
        fixed_company_product_id,
        'company-product-monthly-economic-reports',
        'conciliation',
      ],
      {
        queryParams: current_query_params,
      }
    );
  }

  navigateToInvoiceConciliation(company_product_id: number | null): void {
    if (company_product_id === null) {
      return;
    }

    const fixed_company_product_id = this.fixed_company_product_id();
    if (fixed_company_product_id !== null) {
      this.navigateToCompanyProductConciliation();
      return;
    }

    this.navigateToConciliation(company_product_id);
  }

  openInvoiceConciliationsDialog(row: BillingInvoiceSerialized): void {
    const show_invoice_line = row.period_conciliations.some(conciliation => conciliation.invoice_line_label !== null);

    this.dialog.open(HtmlDialogComponent, {
      width: show_invoice_line ? '720px' : '520px',
      data: {
        title: `Conciliaciones — ${row.invoice_number}`,
        html: this.dom_sanitizer.bypassSecurityTrustHtml(buildInvoiceConciliationsDialogHtml(row.period_conciliations)),
        dialog_close_button: 'Cerrar',
      },
    });
  }

  private filterInvoicesByDateRange(invoices: Invoice[], selected_month: string, is_multi_month_view: boolean): Invoice[] {
    if (is_multi_month_view) {
      const date_range = buildConciliationDateRange();
      return invoices.filter(invoice =>
        isInvoiceDateWithinConciliationRange(invoice.invoice_date, date_range.billed_from, date_range.billed_to)
      );
    }

    return invoices.filter(invoice => this.invoiceBelongsToSelectedBilledMonth(invoice, selected_month));
  }

  private invoiceBelongsToSelectedBilledMonth(invoice: Invoice, selected_month: string): boolean {
    if (dayjs(invoice.invoice_date).format('YYYY-MM') === selected_month) {
      return true;
    }

    return (invoice.company_product_monthly_economic_report_invoice_conciliations ?? []).some(conciliation => {
      const billed_start_date = conciliation.company_product_monthly_economic_report?.billed_start_date;
      return billed_start_date !== undefined && dayjs(billed_start_date).format('YYYY-MM') === selected_month;
    });
  }

  private serializeInvoice(
    invoice: Invoice,
    company_products_by_holded_account: Map<string, CompanyProductLookup>
  ): BillingInvoiceSerialized {
    const company_product_info = this.resolveInvoiceCompanyProduct(invoice, company_products_by_holded_account);
    const total_hours = Number(invoice.ro_total_hours ?? sumInvoiceLineHours(invoice));
    const hours_conciliated = Number(invoice.ro_hours_conciliated ?? 0);
    const period_conciliations = this.serializeInvoicePeriodConciliations(invoice);
    const document_status = buildInvoiceDocumentStatus(invoice);
    const is_conciliable = invoice.is_conciliable ?? true;

    return {
      id: invoice.id,
      company_product_id: company_product_info?.id ?? null,
      company_product_title: company_product_info?.title ?? '—',
      company_product_logo_url: company_product_info?.logo_url ?? null,
      month_label: formatMonthLabel(dayjs(invoice.invoice_date).format('YYYY-MM')),
      invoice_number: invoice.invoice_number,
      document_status_badge: document_status.badge,
      document_status_tooltip: document_status.tooltip,
      document_status_class: document_status.css_class,
      hours_billed_label: this.formatHours(String(total_hours)),
      hours_conciliated_label: this.formatHours(String(hours_conciliated)),
      is_conciliation_pending: is_conciliable && !(invoice.ro_is_fully_conciliated ?? false),
      period_conciliations,
    };
  }

  private resolveInvoiceCompanyProduct(
    invoice: Invoice,
    company_products_by_holded_account: Map<string, CompanyProductLookup>
  ): CompanyProductLookup | null {
    const conciliation_company_products = (invoice.company_product_monthly_economic_report_invoice_conciliations ?? [])
      .map(conciliation => conciliation.company_product_monthly_economic_report?.company_product)
      .filter((company_product): company_product is CompanyProduct => company_product !== undefined && company_product !== null);

    if (conciliation_company_products.length > 0) {
      const company_product = conciliation_company_products[0];
      return {
        id: company_product.id,
        title: company_product.title,
        logo_url: company_product.logo_url ?? null,
      };
    }

    for (const line of invoice.invoice_lines ?? []) {
      if (!line.holded_accounting_account_id) {
        continue;
      }

      const company_product = company_products_by_holded_account.get(line.holded_accounting_account_id);
      if (company_product !== undefined) {
        return company_product;
      }
    }

    return null;
  }

  private serializeInvoicePeriodConciliations(invoice: Invoice): InvoicePeriodConciliationRow[] {
    const invoice_lines_by_id = new Map((invoice.invoice_lines ?? []).map(line => [line.id, line]));

    return (invoice.company_product_monthly_economic_report_invoice_conciliations ?? []).map(conciliation => {
      const billed_start_date = conciliation.company_product_monthly_economic_report?.billed_start_date ?? '';
      const month_label = billed_start_date ? formatMonthLabel(dayjs(billed_start_date).format('YYYY-MM')) : 'Periodo desconocido';
      const invoice_line =
        conciliation.invoice_line ??
        (conciliation.invoice_line_id !== null && conciliation.invoice_line_id !== undefined
          ? (invoice_lines_by_id.get(conciliation.invoice_line_id) ?? null)
          : null);

      return {
        period_label: month_label,
        invoice_line_label: buildInvoiceLineLabel(invoice_line),
        hours_conciliated_label: this.formatHours(conciliation.hours_conciliated),
        amount_conciliated_label: `${Number(conciliation.amount_conciliated).toFixed(2)} €`,
        origin_label: conciliation.is_automatic ? 'Automática' : 'Manual',
      };
    });
  }

  private serializeReports(
    reports: CompanyProductMonthlyEconomicReport[],
    selected_month: string,
    is_multi_month_view: boolean
  ): BillingPeriodSerialized[] {
    return reports
      .filter(report => is_multi_month_view || dayjs(report.billed_start_date).format('YYYY-MM') === selected_month)
      .sort((left, right) => dayjs(right.billed_start_date).valueOf() - dayjs(left.billed_start_date).valueOf())
      .map(report => this.serializeReport(report));
  }

  private serializeReport(report: CompanyProductMonthlyEconomicReport): BillingPeriodSerialized {
    const payment_status = this.buildPaymentStatusInfo(report.payment_status_color);
    const conciliation_status = this.buildConciliationStatusInfo(report);
    const hours_expected = this.parseHoursValue(report.hours_expected_to_register_at_start_of_month);
    const hours_registered = this.parseHoursValue(report.hours_registered_at_end_of_month);
    const hours_billed = this.parseHoursValue(String(report.ro_total_hours_billed ?? 0));
    const shared_max = Math.max(hours_expected, hours_registered, hours_billed);

    return {
      id: report.id,
      company_product_id: report.company_product_id,
      company_product_title: report.company_product?.title ?? `Producto #${report.company_product_id}`,
      company_product_logo_url: report.company_product?.logo_url ?? null,
      billed_start_date: report.billed_start_date,
      month_label: formatMonthLabel(dayjs(report.billed_start_date).format('YYYY-MM')),
      hours_expected_label: this.formatHours(report.hours_expected_to_register_at_start_of_month),
      hours_registered_label: this.formatHours(report.hours_registered_at_end_of_month),
      hours_billed_label: this.formatHours(String(report.ro_total_hours_billed ?? 0)),
      hours_expected_progress: this.calculateProgress(hours_expected, shared_max),
      hours_registered_progress: this.calculateProgress(hours_registered, shared_max),
      hours_billed_progress: this.calculateProgress(hours_billed, shared_max),
      real_price_per_hour_label: this.formatPricePerHour(report.real_average_price_per_hour ?? null),
      invoice_price_per_hour_label: this.formatPricePerHour(report.invoice_average_price_per_hour ?? null),
      payment_status_label: payment_status.label,
      payment_status_class: payment_status.class,
      conciliation_status_label: conciliation_status.label,
      conciliation_status_class: conciliation_status.class,
      payment_status_icon: payment_status.icon,
      payment_status_tooltip: payment_status.tooltip,
    };
  }

  private buildSummaryCards(
    reports: CompanyProductMonthlyEconomicReport[],
    selected_month: string,
    is_multi_month_view: boolean
  ): BillingSummaryCards {
    const relevant_reports = is_multi_month_view
      ? reports
      : reports.filter(report => dayjs(report.billed_start_date).format('YYYY-MM') === selected_month);

    let missing_invoice_count = 0;
    let overdue_payment_count = 0;
    let pending_conciliation_count = 0;

    for (const report of relevant_reports) {
      if (report.ro_should_have_invoice_been_created) {
        missing_invoice_count += 1;
      }
      if (report.payment_status_color === 'yellow') {
        overdue_payment_count += 1;
      }
      if (!report.ro_conciliated) {
        pending_conciliation_count += 1;
      }
    }

    return {
      missing_invoice_count,
      overdue_payment_count,
      pending_conciliation_count,
    };
  }

  private buildPaymentStatusInfo(color: CompanyProductMonthlyEconomicReport['payment_status_color']): {
    label: string;
    class: string;
    icon: string | null;
    tooltip: string | null;
  } {
    switch (color) {
      case 'green':
        return { label: 'Pagado', class: 'status-green', icon: 'check_circle', tooltip: 'Todas las facturas pagadas' };
      case 'yellow':
        return { label: 'Vencido', class: 'status-yellow', icon: 'warning', tooltip: 'Facturas vencidas sin pagar' };
      case 'grey':
        return { label: 'Pendiente', class: 'status-grey', icon: 'schedule', tooltip: 'Pago pendiente sin vencer' };
      default:
        return { label: '—', class: 'status-neutral', icon: null, tooltip: null };
    }
  }

  private buildConciliationStatusInfo(report: CompanyProductMonthlyEconomicReport): { label: string; class: string } {
    if (report.ro_should_have_invoice_been_created) {
      return { label: '—', class: 'status-neutral' };
    }

    const is_conciliated = report.ro_conciliated ?? false;
    if (is_conciliated) {
      return { label: 'Conciliado', class: 'status-green' };
    }

    const has_conciliations =
      (report.company_product_monthly_economic_report_invoice_conciliations?.length ?? 0) > 0 ||
      Math.abs(Number(report.ro_total_hours_billed ?? 0)) > 0;
    if (has_conciliations) {
      return { label: 'Desviación', class: 'status-yellow' };
    }

    return { label: 'Pendiente', class: 'status-grey' };
  }

  private formatHours(value: string | null): string {
    if (value === null || value === '') {
      return '—';
    }
    return `${Number(value).toFixed(2)}h`;
  }

  private formatPricePerHour(value: number | null): string {
    if (value === null) {
      return '—';
    }
    return `${value.toFixed(2)} €`;
  }

  private parseHoursValue(value: string | null): number {
    if (value === null || value === '') {
      return 0;
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed) || parsed < 0) {
      return 0;
    }

    return parsed;
  }

  private calculateProgress(value: number, shared_max: number): number {
    if (shared_max <= 0 || value <= 0) {
      return 0;
    }

    return Math.min(100, (value / shared_max) * 100);
  }

  private parseOptionalNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
}

const COMPANY_PRODUCT_MONTHLY_ECONOMIC_REPORT_BILLING_PERIODS_APPEND =
  'invoice_average_price_per_hour,real_average_price_per_hour,ro_total_hours_billed,ro_conciliated,ro_invoice_issued,ro_should_have_invoice_been_created,ro_expected_invoice_doc_month,payment_status_color';

function buildCompanyProductsByHoldedAccountMap(company_products: CompanyProduct[]): Map<string, CompanyProductLookup> {
  const company_products_by_holded_account = new Map<string, CompanyProductLookup>();

  for (const company_product of company_products) {
    if (!company_product.holded_accounting_account_id) {
      continue;
    }

    company_products_by_holded_account.set(company_product.holded_accounting_account_id, {
      id: company_product.id,
      title: company_product.title,
      logo_url: company_product.logo_url ?? null,
    });
  }

  return company_products_by_holded_account;
}

function sumInvoiceLineHours(invoice: Invoice): number {
  return (invoice.invoice_lines ?? []).reduce((total, line) => total + Number(line.quantity), 0);
}

export function buildInvoiceConciliationsDialogHtml(conciliations: InvoicePeriodConciliationRow[]): string {
  if (conciliations.length === 0) {
    return '<p>Sin conciliaciones registradas.</p>';
  }

  const show_invoice_line = conciliations.some(conciliation => conciliation.invoice_line_label !== null);
  const cell_style = 'padding:8px 10px;border-bottom:1px solid #d7d7d7;';
  const header_style = `${cell_style}text-align:left;`;
  const line_header = show_invoice_line ? `<th style="${header_style}">Línea</th>` : '';

  const rows = conciliations
    .map(conciliation => {
      const line_cell = show_invoice_line
        ? `<td style="${cell_style}word-break:break-word;">${escapeHtml(conciliation.invoice_line_label ?? '—')}</td>`
        : '';

      return `
        <tr>
          <td style="${cell_style}">${escapeHtml(conciliation.period_label)}</td>
          ${line_cell}
          <td style="${cell_style}">${escapeHtml(conciliation.hours_conciliated_label)}</td>
          <td style="${cell_style}">${escapeHtml(conciliation.amount_conciliated_label)}</td>
          <td style="${cell_style}">${escapeHtml(conciliation.origin_label)}</td>
        </tr>
      `;
    })
    .join('');

  return `
    <table style="width:100%;border-collapse:collapse;">
      <thead>
        <tr>
          <th style="${header_style}">Periodo</th>
          ${line_header}
          <th style="${header_style}">Horas</th>
          <th style="${header_style}">Importe</th>
          <th style="${header_style}">Origen</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

function buildInvoiceDocumentStatus(invoice: Invoice): { badge: string | null; tooltip: string | null; css_class: string | null } {
  if (invoice.is_credit_note || invoice.document_type === 'credit_note') {
    const origin_number = invoice.rectifies_invoice?.invoice_number;
    return {
      badge: 'Rectificativa',
      tooltip: origin_number ? `Rectifica ${origin_number}` : 'Factura rectificativa (credit note)',
      css_class: 'invoice-status-badge invoice-status-credit-note',
    };
  }

  if (invoice.is_cancelled || invoice.holded_status === 'cancelled') {
    return {
      badge: 'Anulada',
      tooltip: 'Factura anulada en Holded',
      css_class: 'invoice-status-badge invoice-status-cancelled',
    };
  }

  if (invoice.is_rectified) {
    const credit_note_number = invoice.rectified_by_credit_notes?.[0]?.invoice_number;
    return {
      badge: 'Rectificada',
      tooltip: credit_note_number ? `Anulada por ${credit_note_number}` : 'Factura rectificada por un credit note',
      css_class: 'invoice-status-badge invoice-status-rectified',
    };
  }

  return { badge: null, tooltip: null, css_class: null };
}

function escapeHtml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}
